// Angular
import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Ionic
import { ModalController, ToastController } from '@ionic/angular';

// NgRx
import { Store } from '@ngrx/store';
import { addIcons } from 'ionicons';
import {
  homeOutline,
  diamondOutline,
  businessOutline,
  lockClosedOutline,
  constructOutline,
  logOutOutline,
  calendarOutline,
  schoolOutline,
  restaurantOutline,
  trophyOutline,
  trendingUpOutline,
  trailSignOutline,
  gitNetworkOutline,
  bagHandleOutline,
  ticketOutline,
  barbellOutline,
  clipboardOutline,
  bedOutline,
  sunnyOutline,
  shieldCheckmarkOutline,
  notificationsOutline,
  qrCodeOutline,
  settingsOutline,
  storefrontOutline,
  chatbubblesOutline,
  peopleOutline,
  idCardOutline,
  walletOutline,
  alertCircleOutline,
  barChartOutline,
} from 'ionicons/icons';
import { filter, first, map, Observable } from 'rxjs';

// Capacitor
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import {
  AppTrackingStatusResponse,
  AppTrackingTransparency,
} from 'capacitor-plugin-app-tracking-transparency';

// Lessor
import packageJson from '@lessor/packagejson';
import { NavigationService, ResolvedNavigationItem } from './core/navigation';

// Libs
import { AnalyticsService, openAnalyticsConsent } from '@libs/analytics';
import { AuthFacade } from '@libs/auth-lib';
import { CrashlyticsService } from '@libs/crashlytics';
import {
  Canal,
  LessorUser,
  LocalStorageItem,
  RequiredRole,
  SettingsSocialSport,
} from '@libs/entity-lib';
import { NotificationListenersService } from '@libs/notifications';
import {
  selectLoggedUserPhoto,
  selectResponsavelName,
  selectSportCenterId,
  selectSportCenterImage,
  selectSportCenterName,
  selectSportCenterUsername,
  setProfileLessor,
  setSettingsSocialSport,
} from '@libs/settings-state';
import { lessorBrand, lessorEnvironment } from '@libs/shared/util-environments';
import { TranslationLibModule } from '@libs/translation';
import {
  CapgoService,
  PlatformDetectionService,
  ServiceWorkerService,
  VibrationService,
} from '@libs/util-services';

// Others
import { configureIcons } from './config/icon-config';
import { DialogService } from './core/dialogs/dialog.service';
import { UtilService } from './core/services/util/util.service';
import { SummarizeNamePipeComponent } from './summarize-name.pipe';

// Swiper
import { SwUpdate } from '@angular/service-worker';
import { MaintenanceApiService } from '@libs/maintenance';
import { register } from 'swiper/element/bundle';

register();

import {
  IonApp,
  IonBadge,
  IonButton,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonSplitPane,
} from '@ionic/angular/standalone';

@Component({
  selector: 'lessor-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    IonApp,
    IonBadge,
    IonButton,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonMenu,
    IonMenuToggle,
    IonRouterOutlet,
    IonSplitPane,
    CommonModule,
    TranslationLibModule,
    SummarizeNamePipeComponent,
  ],
  providers: [CrashlyticsService, UtilService, DialogService, ServiceWorkerService],
})
export class AppComponent {
  private readonly crashlyticsService = inject(CrashlyticsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly modalController = inject(ModalController);
  private readonly authFacade = inject(AuthFacade);
  private readonly vibrationService = inject(VibrationService);
  private readonly router = inject(Router);
  private readonly platformDetectionService = inject(PlatformDetectionService);
  private readonly notificationListenersService = inject(NotificationListenersService);
  private readonly maintenanceService = inject(MaintenanceApiService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly serviceWorkerService = inject(ServiceWorkerService);
  private readonly capgoService = inject(CapgoService);
  private readonly toastController = inject(ToastController);
  private readonly appRef = inject(ApplicationRef);

  private readonly store = inject(Store);
  protected readonly nav = inject(NavigationService);

  selectSportCenterUsername = this.store.selectSignal(selectSportCenterUsername);
  selectSportCenterName = this.store.selectSignal(selectSportCenterName);
  selectSportCenterImage = this.store.selectSignal(selectSportCenterImage);

  /** Foto da pessoa logada, quando existe de verdade (ver selectLoggedUserPhoto). */
  private readonly loggedUserPhoto = this.store.selectSignal(selectLoggedUserPhoto);

  /**
   * Avatar do menu: foto da PESSOA primeiro (é o nome dela que aparece ao lado),
   * logo do centro esportivo como reserva.
   */
  readonly avatarImage = computed(
    () => this.loggedUserPhoto() || this.selectSportCenterImage() || null
  );

  version = packageJson?.version ?? '0.0.0';

  selectResponsavelName = this.store.selectSignal(selectResponsavelName);

  isDevelopment = signal(!lessorEnvironment.production);
  environmentTitle = signal(lessorEnvironment.title);

  isPWA = signal(this.platformDetectionService.isPWA());

  private readonly currentUrl = signal(this.router.url);

  selectSportCenterId = this.store.selectSignal(selectSportCenterId);

  requiredRole = RequiredRole;

  constructor() {
    // Ionic standalone não registra ícones sozinho: sem isto os ion-icon do
    // menu lateral (ionicons dos itens de navegação) renderizam vazios.
    addIcons({
      homeOutline,
      diamondOutline,
      businessOutline,
      lockClosedOutline,
      constructOutline,
      logOutOutline,
      calendarOutline,
      schoolOutline,
      restaurantOutline,
      trophyOutline,
      trendingUpOutline,
      trailSignOutline,
      gitNetworkOutline,
      bagHandleOutline,
      ticketOutline,
      barbellOutline,
      clipboardOutline,
      bedOutline,
      sunnyOutline,
      shieldCheckmarkOutline,
      notificationsOutline,
      qrCodeOutline,
      settingsOutline,
      storefrontOutline,
      chatbubblesOutline,
      peopleOutline,
      idCardOutline,
      walletOutline,
      alertCircleOutline,
      barChartOutline,
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));

    this.configureNativeChrome();
    this.scheduleLoadingOverlayFadeOut();
    this.hidSplashScreen();
    this.init();

    // Previously in ngOnInit
    this.requestGeolocationPermission();
    this.crashlyticsService.initFb();
    this.notificationListenersService.init();
    // Carrega consentimento persistido (default OFF) e, no 1º uso, abre a tela cheia (LGPD).
    this.analyticsService.init().then(() => {
      if (this.analyticsService.needsConsent()) {
        openAnalyticsConsent(this.modalController, this.router);
      }
    });
    this.checkForUpdates();

    // Hidrata tokens de notificação do backend quando o usuário estiver autenticado.
    // `onAuthenticated()` é idempotente. Destrava o banner de ativação no
    // NotificationPermissionPromptService (anti-flash).
    effect(() => {
      if (this.authFacade.isLoggedIn()) {
        this.notificationListenersService.onAuthenticated();
      }
    });
  }

  hidSplashScreen() {
    SplashScreen.hide();
  }

  /**
   * Aplica light-only nas APIs nativas (StatusBar) — coerente com ADR de tema.
   * Lessor tem splash carvão azulado (#0E1113) → StatusBar style 'light' (texto branco).
   * Valores vêm do brand.ts (fonte única).
   */
  private configureNativeChrome(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const style = lessorBrand.statusBarStyle === 'dark' ? Style.Dark : Style.Light;
    StatusBar.setStyle({ style }).catch(() => {
      // setStyle pode falhar em web/PWA — não-fatal
    });
    if (Capacitor.getPlatform() === 'android') {
      StatusBar.setBackgroundColor({ color: lessorBrand.statusBarBackgroundColor }).catch(() => {
        // setBackgroundColor é Android-only — ignora se não suportar
      });
    }
  }

  /**
   * HTML splash overlay (index.html) some quando o Angular estabiliza, evitando o flash
   * entre o splash nativo (que esconde após SplashScreen.hide()) e o primeiro paint do app.
   */
  private scheduleLoadingOverlayFadeOut(): void {
    const overlay = document.getElementById('app-loading-overlay');
    if (!overlay) {
      return;
    }
    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first()
      )
      .subscribe(() => {
        overlay.classList.add('app-loading-overlay--hidden');
        setTimeout(() => overlay.remove(), 350);
      });
  }

  checkForUpdates() {
    if (Capacitor.getPlatform() === 'web') {
      // Para PWA/Web - usa Service Worker
      this.serviceWorkerService.checkForUpdates();
    }

    // Para mobile
    // Capgo Updates
    this.initCapacitorUpdater();
  }

  init() {
    // Load Icons
    configureIcons();
    this.setSettingsSocialSport();
    this.initPathName();

    this.maintenanceService.checkMaintenance(Canal.SPORT_CENTER);
  }

  initPathName(): void {
    localStorage.setItem(LocalStorageItem.INIT_APPLICATION_WITH_PATHNAME, window.location.pathname);
  }

  setSettingsSocialSport() {
    const settings = {
      canal: Canal.SPORT_CENTER,
      sportCenterUsername: this.selectSportCenterUsername(),
    } as SettingsSocialSport;
    this.store.dispatch(setSettingsSocialSport({ settings }));
  }

  async logout() {
    this.vibrationService.hapticsImpactMedium();
    this.authFacade.logout();
    this.forceCloseMenu();
    this.store.dispatch(setProfileLessor({ lessorUser: {} as LessorUser }));
    this.analyticsService.resetUserId();
  }

  forceCloseMenu() {
    const menu = document.querySelector('ion-menu#lessorMenuId') as HTMLIonMenuElement | null;
    if (menu?.close) {
      menu.close();
    }
  }

  public async getStatus(): Promise<AppTrackingStatusResponse> {
    const response = await AppTrackingTransparency.getStatus();

    // { status: 'authorized' } for example
    return response;
  }

  async requestGeolocationPermission() {
    if (Capacitor.getPlatform() !== 'web') {
      await Geolocation.requestPermissions();
    }
  }

  redirectTo(link: string) {
    if (!link) return;
    // `navigateByUrl` parseia URL completa incluindo query string (`?tab=...`)
    // e fragment. `navigate(['/foo?bar=baz'])` trata tudo como segmento e
    // encoda `?` como `%3F`, virando 404. Os items do menu (ex: loja de
    // módulos `/subscription?tab=store`) precisam dessa parsing.
    this.router.navigateByUrl(link);
  }

  redirectPricingPage() {
    this.forceCloseMenu();
    this.router.navigate(['subscription']);
  }

  redirectMultiAccount() {
    this.forceCloseMenu();
    this.router.navigate(['/multi-contas']);
  }

  async showUpdateToast() {
    const toast = await this.toastController.create({
      message: 'Uma nova versão está disponível!',
      position: 'bottom',
      buttons: [
        {
          text: 'Atualizar',
          handler: () => {
            this.swUpdate.activateUpdate().then(() => window.location.reload());
          },
        },
      ],
    });
    await toast.present();
  }

  async initCapacitorUpdater() {
    await this.capgoService.initialize({
      isProduction: lessorEnvironment.production,
      enableDebugToasts: true,
    });
  }

  hasPermission$(requiredRoles: RequiredRole[]): Observable<boolean> {
    return this.authFacade.selectRoles$.pipe(
      map((userRoles) => requiredRoles.some((role) => userRoles.includes(role)))
    );
  }

  /**
   * Seções do side menu derivadas do NavigationService.
   * Ordem: Início, Meus módulos, Pessoas, Financeiro, Operações, Insights, Mais.
   */
  readonly sideMenuSections = computed<{ titleKey: string; items: ResolvedNavigationItem[] }[]>(
    () => {
      const grouped = this.nav.sideMenuByCategory();
      // Sub-item de hub não é módulo: barra o vazamento para "Meus módulos".
      const myModules = this.nav
        .activeModuleItems()
        .filter((i) => i.surface !== 'AGENDAMENTOS_HUB' && i.surface !== 'AULAS_HUB');
      const sections = [
        { titleKey: 'LESSOR.NAVIGATION.SECTION.MY_MODULES', items: myModules },
        { titleKey: 'LESSOR.NAVIGATION.SECTION.PEOPLE', items: grouped.PEOPLE },
        { titleKey: 'LESSOR.NAVIGATION.SECTION.FINANCE', items: grouped.FINANCE },
        { titleKey: 'LESSOR.NAVIGATION.SECTION.OPERATIONS', items: grouped.OPERATIONS },
        { titleKey: 'LESSOR.NAVIGATION.SECTION.INSIGHTS', items: grouped.INSIGHTS },
        { titleKey: 'LESSOR.NAVIGATION.SECTION.MORE', items: grouped.ADDON },
      ];
      return sections.filter((s) => s.items.length > 0);
    }
  );

  readonly storeCtaItem = computed<ResolvedNavigationItem | null>(() => {
    const items = this.nav.sideMenuByCategory().MARKETPLACE;
    return items.length > 0 ? items[0] : null;
  });

  /**
   * Verifica se a rota está ativa
   */
  isActiveRoute(url: string | undefined): boolean {
    if (!url) return false;
    const currentUrl = this.currentUrl();

    // Normalizar URLs removendo barras duplas e fragmentos
    const normalizedUrl = url.replace(/\/+/g, '/').split('?')[0].split('#')[0];
    const normalizedCurrentUrl = currentUrl.replace(/\/+/g, '/').split('?')[0].split('#')[0];

    // Verificar correspondência exata ou se é uma subrota
    return (
      normalizedCurrentUrl === normalizedUrl || normalizedCurrentUrl.startsWith(normalizedUrl + '/')
    );
  }

  redirectToHome(): void {
    this.forceCloseMenu();
    this.router.navigateByUrl('/home');
  }
}
